/**
 * Side-anchored Workflow Test drawer (P4.T8).
 *
 * Operator configures a fixture (slots JSON, triggers JSON, expected
 * final status), clicks Run, and watches the trace. Paused state
 * surfaces a Fire-event sub-form (event_name + correlation readonly;
 * payload JSON editor pre-filled from localStorage). Abort cancels a
 * running or paused test. Restart returns to idle after terminal/aborted.
 *
 * All backend calls flow through useWorkflowTest — no direct apiClient
 * import here.
 *
 * Implementation notes:
 * - The shared Drawer primitive, resizable (SA-814), always-open
 *   (parent controls mount/unmount).
 * - localStorage persistence uses two keys per spec §3.9:
 *     workflow-test-fixture::{bundleId}::{workflowId}
 *     workflow-test-event-payload::{bundleId}::{workflowId}
 *   Empty-on-blur clears the relevant entry (T11 pattern).
 * - Trace rendering per spec §4.2: sandbox events (would_have_fired,
 *   would_have_called_tool) → gray italic + 🪶 + "Sandboxed — no external
 *   effect."; execution_error → red; others → default text.
 *
 * Spec ref: §3.8 + §3.9 + §3.10 + §4.1 + §4.2 + §4.3 + §4.4.
 */
import { useEffect, useMemo, useState } from 'react'

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/Drawer'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import {
  useWorkflowTest,
  type WorkflowStatus,
  type WorkflowTestEvent,
} from '@/hooks/useWorkflowTest'

// ── localStorage keys ────────────────────────────────────────────────────────

const FIXTURE_KEY = (bundleId: string, workflowId: string) =>
  `workflow-test-fixture::${bundleId}::${workflowId}`

const PAYLOAD_KEY = (bundleId: string, workflowId: string) =>
  `workflow-test-event-payload::${bundleId}::${workflowId}`

// ── Types ────────────────────────────────────────────────────────────────────

type FixtureState = {
  slots: string
  triggers: string
  expected_final_status: WorkflowStatus | null
}

const DEFAULT_FIXTURE: FixtureState = {
  slots: '{}',
  triggers: '{}',
  expected_final_status: null,
}

export type WorkflowTestDrawerProps = {
  bundleId: string
  workflowId: string
  workflowName: string
  onClose: () => void
}

// ── Component ────────────────────────────────────────────────────────────────

export function WorkflowTestDrawer({
  bundleId,
  workflowId,
  workflowName,
  onClose,
}: WorkflowTestDrawerProps) {
  const test = useWorkflowTest({ bundleId, workflowId })
  const resize = useDrawerWidth('workflow-test', { default: 480, min: 480 }, 'Resize workflow test panel')

  // Fixture state (persisted in localStorage)
  const [fixture, setFixture] = useState<FixtureState>(() => {
    try {
      const stored = window.localStorage.getItem(FIXTURE_KEY(bundleId, workflowId))
      return stored ? (JSON.parse(stored) as FixtureState) : DEFAULT_FIXTURE
    } catch {
      return DEFAULT_FIXTURE
    }
  })

  const writeFixture = (next: FixtureState) => {
    setFixture(next)
    if (!next.slots && !next.triggers && !next.expected_final_status) {
      window.localStorage.removeItem(FIXTURE_KEY(bundleId, workflowId))
    } else {
      window.localStorage.setItem(FIXTURE_KEY(bundleId, workflowId), JSON.stringify(next))
    }
  }

  const handleBlur = () => {
    // Empty-on-blur: if slots (primary field) is empty, clear the entire entry
    if (fixture.slots === '') {
      window.localStorage.removeItem(FIXTURE_KEY(bundleId, workflowId))
    }
  }

  // Payload state for fire-event sub-form
  const [payload, setPayload] = useState<string>('{}')
  useEffect(() => {
    if (test.paused?.event_name) {
      try {
        const stored = window.localStorage.getItem(PAYLOAD_KEY(bundleId, workflowId))
        if (stored) {
          const parsed = JSON.parse(stored) as { lastPayloadByEventName: Record<string, string> }
          const remembered = parsed.lastPayloadByEventName?.[test.paused.event_name]
          if (remembered) setPayload(remembered)
        }
      } catch {
        // noop — localStorage read failure is non-fatal
      }
    }
  }, [test.paused?.event_name, bundleId, workflowId])

  // JSON validation
  const slotsValid = useMemo(() => safeParseJSON(fixture.slots) !== undefined, [fixture.slots])
  const triggersValid = useMemo(() => safeParseJSON(fixture.triggers) !== undefined, [fixture.triggers])
  const fixtureValid = slotsValid && triggersValid

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleRun = () => {
    test.start.mutate({
      slots: (safeParseJSON(fixture.slots) as Record<string, unknown>) ?? {},
      triggers: (safeParseJSON(fixture.triggers) as Record<string, unknown>) ?? {},
      expected_final_status: fixture.expected_final_status ?? undefined,
    })
  }

  const handleFire = () => {
    if (!test.paused) return
    const parsed = (safeParseJSON(payload) as Record<string, unknown>) ?? {}
    // Persist payload by event_name for future pre-fill
    try {
      const stored = window.localStorage.getItem(PAYLOAD_KEY(bundleId, workflowId))
      const map = (stored
        ? (JSON.parse(stored) as { lastPayloadByEventName: Record<string, string> })
        : { lastPayloadByEventName: {} })
      map.lastPayloadByEventName[test.paused.event_name] = payload
      window.localStorage.setItem(PAYLOAD_KEY(bundleId, workflowId), JSON.stringify(map))
    } catch {
      // noop
    }
    test.fireEvent.mutate({
      event_name: test.paused.event_name,
      correlation: test.paused.correlation,
      payload: parsed,
    })
  }

  const handleRestart = () => {
    test.reset()
  }

  const handleAbort = () => {
    test.abort.mutate()
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Drawer open onOpenChange={(open) => { if (!open) onClose() }}>
      <DrawerContent aria-label="Workflow test runner" resize={resize}>
        <DrawerHeader className="items-start">
          <div className="flex-1 min-w-0">
            <DrawerTitle>{workflowName}</DrawerTitle>
            {/* §4.4 clarity touch #1: persistent Test Mode banner */}
            <div
              role="alert"
              className="mt-2 bg-warning/10 border border-warning/30 rounded-sm px-3 py-2 text-xs"
            >
              🧪 <strong>Test Mode — sandboxed execution.</strong>{' '}
              Side effects (messages, charges, tool calls) are stubbed.
              LLM and engine logic are real.
            </div>
          </div>
        </DrawerHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            {/* ── Fixture form ──────────────────────────────────────────── */}
            <section aria-label="Fixture form" className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-foreground block mb-1">
                  Slots (JSON)
                </span>
                <textarea
                  aria-label="Slots"
                  value={fixture.slots}
                  onChange={(e) => writeFixture({ ...fixture, slots: e.target.value })}
                  onBlur={handleBlur}
                  rows={4}
                  className={`w-full rounded border px-2 py-1.5 font-mono text-sm bg-background ${
                    slotsValid
                      ? 'border-border'
                      : 'border-destructive'
                  }`}
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-foreground block mb-1">
                  Triggers (JSON)
                </span>
                <textarea
                  aria-label="Triggers"
                  value={fixture.triggers}
                  onChange={(e) => writeFixture({ ...fixture, triggers: e.target.value })}
                  onBlur={handleBlur}
                  rows={3}
                  className={`w-full rounded border px-2 py-1.5 font-mono text-sm bg-background ${
                    triggersValid
                      ? 'border-border'
                      : 'border-destructive'
                  }`}
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-foreground block mb-1">
                  Expected Final Status
                </span>
                <select
                  aria-label="Expected Final Status"
                  value={fixture.expected_final_status ?? ''}
                  onChange={(e) => {
                    const v = e.target.value as WorkflowStatus | ''
                    writeFixture({ ...fixture, expected_final_status: v || null })
                  }}
                  className="w-full rounded-sm border border-border px-2 py-1.5 text-sm bg-background"
                >
                  <option value="">(any)</option>
                  <option value="completed">completed</option>
                  <option value="failed">failed</option>
                  <option value="paused">paused</option>
                  <option value="abandoned">abandoned</option>
                </select>
              </label>
            </section>

            {/* ── Toolbar ──────────────────────────────────────────────── */}
            <div className="flex gap-2">
              {test.state !== 'terminal' && test.state !== 'aborted' && (
                <button
                  onClick={handleRun}
                  disabled={
                    !fixtureValid ||
                    test.state === 'running' ||
                    test.state === 'paused'
                  }
                  className="px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 hover:bg-primary/90 disabled:cursor-not-allowed"
                >
                  Run
                </button>
              )}
              {(test.state === 'terminal' || test.state === 'aborted') && (
                <button
                  onClick={handleRestart}
                  className="px-3 py-1.5 rounded-sm bg-muted text-sm font-medium hover:bg-muted-foreground/10"
                >
                  Restart
                </button>
              )}
              {(test.state === 'running' || test.state === 'paused') && (
                <button
                  onClick={handleAbort}
                  className="px-3 py-1.5 rounded-sm bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20"
                >
                  Abort
                </button>
              )}
            </div>

            {/* §4.4 clarity touch #2: one-time ephemeral notice */}
            {test.state === 'idle' && test.events.length === 0 && (
              <p className="text-xs text-muted-foreground">
                This is a one-time test run. Closing the drawer or starting a
                new Run will discard the active test instance and its synthetic
                conversation. Fixture values are saved locally to your browser.
              </p>
            )}

            {/* ── Trace panel ───────────────────────────────────────────── */}
            {test.events.length > 0 && (
              <TracePanel events={test.events} traceTruncated={test.traceTruncated} />
            )}

            {/* ── Fire-event sub-form (paused state only) ──────────────── */}
            {test.state === 'paused' && test.paused && (
              <section aria-label="Fire event" className="border-t border-border pt-4 space-y-3">
                <p className="text-xs font-medium text-foreground">
                  Waiting for event — fire it to resume the test:
                </p>
                <label className="block">
                  <span className="text-xs font-medium text-foreground block mb-1">
                    Event name
                  </span>
                  <input
                    aria-label="Event name"
                    value={test.paused.event_name}
                    readOnly
                    className="w-full rounded-sm border border-border px-2 py-1.5 bg-muted font-mono text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-foreground block mb-1">
                    Correlation
                  </span>
                  <input
                    aria-label="Correlation"
                    value={test.paused.correlation}
                    readOnly
                    className="w-full rounded-sm border border-border px-2 py-1.5 bg-muted font-mono text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-foreground block mb-1">
                    Payload (JSON)
                  </span>
                  <textarea
                    aria-label="Payload"
                    value={payload}
                    onChange={(e) => setPayload(e.target.value)}
                    rows={4}
                    className="w-full rounded-sm border border-border px-2 py-1.5 font-mono text-sm bg-background"
                  />
                </label>
                <button
                  onClick={handleFire}
                  className="px-3 py-1.5 rounded-sm bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
                >
                  Fire
                </button>
              </section>
            )}

            {/* ── Pass/Fail badge ───────────────────────────────────────── */}
            {test.state === 'terminal' && test.passed !== null && (
              <div
                className={`rounded px-3 py-2 text-sm font-medium ${
                  test.passed
                    ? 'bg-success/10 text-success'
                    : 'bg-destructive/10 text-destructive'
                }`}
              >
                {test.passed ? '✅ Pass' : '❌ Fail'} — actual:{' '}
                {test.finalStatus}, expected: {fixture.expected_final_status ?? '(any)'}
              </div>
            )}
          </div>
      </DrawerContent>
    </Drawer>
  )
}

// ── TracePanel ───────────────────────────────────────────────────────────────

function TracePanel({
  events,
  traceTruncated,
}: {
  events: WorkflowTestEvent[]
  traceTruncated: boolean
}) {
  return (
    <section
      aria-label="Trace"
      className="border border-border rounded-sm p-2 max-h-64 overflow-auto font-mono text-xs space-y-1"
    >
      {events.map((ev) => (
        <TraceRow key={ev.id} ev={ev} />
      ))}
      {traceTruncated && (
        <div className="text-muted-foreground italic">
          Trace truncated at 1000 events. Phase 5: configurable cap +
          load-more affordance.
        </div>
      )}
    </section>
  )
}

// ── TraceRow — per-event rendering (spec §4.2) ────────────────────────────────

function TraceRow({ ev }: { ev: WorkflowTestEvent }) {
  const isSandbox =
    ev.event_type === 'would_have_fired' || ev.event_type === 'would_have_called_tool'

  if (isSandbox) {
    // §4.4 clarity touch #3: sandbox markers
    const icon = ev.event_type === 'would_have_fired' ? '🪶' : '🛠️'
    return (
      <div className="text-muted-foreground italic">
        {icon} <strong>{ev.event_type}:</strong>{' '}
        {String(ev.payload?.node_type ?? '')} —{' '}
        <em>Sandboxed — no external effect.</em>
      </div>
    )
  }

  if (ev.event_type === 'execution_error') {
    return (
      <div className="text-destructive">
        <strong>execution_error:</strong> {String(ev.payload?.message ?? '')}
      </div>
    )
  }

  return (
    <div>
      <strong>{ev.event_type}:</strong> {ev.node_id ?? ''}
    </div>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function safeParseJSON(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return undefined
  }
}
